import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Slash } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  MarcusSlashCommand,
  MarcusSlashCommandCategory,
} from "../../lib/marcusSlashCommands";

interface SlashCommandPaletteProps {
  open: boolean;
  commands: readonly MarcusSlashCommand[];
  activeIndex: number;
  compact?: boolean;
  /** Show category section headers (only sensible when the filter is empty). */
  showCategories?: boolean;
  onSelect: (cmd: MarcusSlashCommand) => void;
  /** Called when the user hovers a row — lets the input sync activeIndex. */
  onHover?: (index: number) => void;
}

const CATEGORY_ORDER: MarcusSlashCommandCategory[] = [
  "static-ads",
  "video-ads",
  "onboarding",
];
const CATEGORY_HEADERS: Record<MarcusSlashCommandCategory, string> = {
  "static-ads": "Static ads",
  "video-ads": "Video ads",
  onboarding: "Onboarding",
};

export function SlashCommandPalette({
  open,
  commands,
  activeIndex,
  compact = false,
  showCategories = false,
  onSelect,
  onHover,
}: SlashCommandPaletteProps) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const grouped = useMemo(() => {
    if (!showCategories) {
      return [
        {
          category: null as MarcusSlashCommandCategory | null,
          items: commands.map((cmd, idx) => ({ cmd, idx })),
        },
      ];
    }
    return CATEGORY_ORDER.map((category) => ({
      category,
      items: commands
        .map((cmd, idx) => ({ cmd, idx }))
        .filter(({ cmd }) => cmd.category === category),
    })).filter((group) => group.items.length > 0);
  }, [commands, showCategories]);

  const hasResults = commands.length > 0;
  const itemPadding = compact ? "px-2.5 py-2" : "px-3 py-3";
  const iconSize = compact ? "h-7 w-7" : "h-8 w-8";
  const iconGlyph = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  const listHeight = compact
    ? "max-h-[40vh] md:max-h-64"
    : "max-h-[40vh] md:max-h-72";

  useEffect(() => {
    if (!open || !hasResults) return;
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, hasResults, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="slash-command-palette"
          role="listbox"
          aria-label="Slash commands"
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.98 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-full left-0 right-0 z-30 mb-2 origin-bottom overflow-hidden rounded-lg bg-surface-lowest text-foreground shadow-ambient-lg"
        >
          <div className="flex items-center justify-between gap-3 bg-surface-lowest px-4 py-3">
            <div className="flex min-w-0 items-center gap-2 ds-caption uppercase text-muted-foreground">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-surface-high text-foreground shadow-ambient">
                <Slash className="h-2.5 w-2.5" />
              </span>
              <span className="truncate">Marcus commands</span>
            </div>
            <span className="shrink-0 rounded-full bg-surface-high px-2.5 py-1 ds-micro text-muted-foreground">
              {hasResults ? `${commands.length} available` : "No matches"}
            </span>
          </div>
          {!hasResults && (
            <div className="px-4 pb-4 pt-2 ds-body-sm text-muted-foreground">
              No matching commands.
            </div>
          )}
          {hasResults && (
            <div
              className={cn(
                "flex flex-col gap-1 overflow-y-auto bg-surface-low p-2 ds-scrollbar",
                listHeight,
              )}
            >
              {grouped.map((group) => (
                <div
                  key={group.category ?? "__flat__"}
                  className="flex flex-col gap-1"
                >
                  {group.category && showCategories && (
                    <div className="px-2 pb-0.5 pt-1.5 ds-caption uppercase text-muted-foreground">
                      {CATEGORY_HEADERS[group.category]}
                    </div>
                  )}
                  {group.items.map(({ cmd, idx }) => {
                    const isActive = idx === activeIndex;
                    const Icon = cmd.icon;
                    return (
                      <button
                        key={cmd.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        ref={(element) => {
                          itemRefs.current[idx] = element;
                        }}
                        onMouseEnter={() => onHover?.(idx)}
                        onMouseDown={(event) => {
                          // Prevent the textarea from losing focus before we
                          // dispatch the selection.
                          event.preventDefault();
                        }}
                        onClick={() => onSelect(cmd)}
                        className={cn(
                          "group relative flex w-full items-center gap-3 rounded-md text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          itemPadding,
                          isActive
                            ? "bg-surface-highest text-foreground shadow-ambient"
                            : "text-foreground/90 hover:bg-surface-container",
                        )}
                      >
                        <span
                          className={`relative inline-flex shrink-0 items-center justify-center rounded-sm transition-colors ${iconSize} ${
                            isActive
                              ? "bg-card text-foreground"
                              : "bg-surface-high text-muted-foreground group-hover:text-foreground"
                          }`}
                        >
                          <Icon className={iconGlyph} />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span
                            className={`truncate font-semibold ${
                              compact ? "text-[12px]" : "text-[13px]"
                            }`}
                          >
                            {cmd.label}
                          </span>
                          <span
                            className={`truncate text-muted-foreground ${
                              compact ? "text-[11px] leading-snug" : "text-xs leading-snug"
                            }`}
                          >
                            {cmd.description}
                          </span>
                        </span>
                        <span
                          className="shrink-0 rounded-full bg-surface-high px-2.5 py-1 ds-micro text-muted-foreground transition-colors group-hover:bg-surface-highest group-hover:text-foreground"
                        >
                          {cmd.categoryLabel}
                        </span>
                        <AnimatePresence>
                          {isActive && (
                            <motion.span
                              key="chev"
                              initial={{ opacity: 0, x: -4 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -4 }}
                              transition={{ duration: 0.12 }}
                              className="shrink-0 text-foreground"
                              aria-hidden
                            >
                              <ChevronRight className={iconGlyph} />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
